import { post, type ApiErr } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { Block, Cell, Grid, Strip, Table, type Act } from '../ui/Block';
import { Head, useAdmin } from '../ui/Shell';
import { confirmSimple } from './dataShared';
import { CreateModal, JobBanner, MachineDrawer, appName, byAge, machCols, machRow, notRoutable, pl, poolKey, replaceSets, stale, useMachineActions, useTick, type EngineJob, type MachineList } from './machines';

export function Engines() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  useTick();
  const list = useData<MachineList>(env, '/admin/machines?app=engine', 5, paused);
  const listEn = useData<MachineList>(env, '/admin/machines?app=engine-en', 5, paused);
  const jobs = useData<{ jobs: EngineJob[] }>(env, '/admin/engines/jobs', 5, paused);
  const { acts, probing } = useMachineActions('engine', list.reload);
  const en = useMachineActions('engine-en', listEn.reload);
  const all = byAge(list.data?.machines ?? []);
  const both = [...all, ...byAge(listEn.data?.machines ?? [])];
  const real = both.filter((m) => !notRoutable(m));
  const genoa = real.filter((m) => m.cpuClass === 'vnni').length;
  const waiting = real.filter((m) => m.updateWhenStopped);
  const elig = [...replaceSets(both.filter((m) => poolKey(m) === 'engine')).elig, ...replaceSets(both.filter((m) => poolKey(m) === 'engine-en')).elig];
  const running = jobs.data?.jobs.find((j) => j.state === 'running') ?? null;
  const name = appName(env, 'engine');
  const flyErr = describeErr(list.error, 'Fly');

  const runDeferred = async () => {
    try {
      const r = await post<{ image: string; updated: string[]; waiting: string[] }>(env, '/admin/engines/deferred');
      list.reload();
      ctx.toast(`Updated ${pl(r.updated.length, 'stopped engine')}`, r.waiting.length ? `${pl(r.waiting.length, 'engine')} still waiting: ${r.waiting.join(', ')}` : 'Every engine is on the current image');
    } catch (e) { ctx.toast('Deferred update failed', (e as ApiErr).message, { logged: false }); }
  };

  const stripActs: Act[] = mobile ? [] : [
    { label: `Replace non-Genoa · ${elig.length}`, title: 'Runs the create loop for each avx2 / avx2-zen3 machine that is stopped and idle', disabled: !elig.length || !!running, onClick: () => ctx.openModal(<CreateModal mode="replace" machines={both} />) },
    { label: running ? 'Create job running…' : 'Create engines', primary: true, onClick: () => ctx.openModal(running ? <CreateModal mode={running.mode} machines={both} jobId={running.id} /> : <CreateModal mode="create" machines={both} />) },
  ];
  if (!mobile && waiting.length) stripActs.unshift({ label: 'Update stopped engines now', title: 'Puts every stopped engine on the current image; started ones wait for their next stop', onClick: () => confirmSimple(ctx, { title: 'Update stopped engines now', app: name, body: 'Every stopped engine is moved to the current image now; started ones wait for their next stop.', confirmLabel: 'Update stopped engines', onConfirm: runDeferred }) });

  return (
    <>
      <Head title="Engines" sub="Whisper int8 with diarization and translation. Fly's proxy starts a machine when a stream arrives and stops it when idle." />
      <Grid>
        <Cell span={12}>
          <Block note={mobile ? 'Create and replace are desktop-only.' : undefined} foot="Engines holding a recording are not replaced by a deploy." loading={list.loading && !list.data} error={!list.data && flyErr ? { ...flyErr, retry: list.reload } : null}>
            <Strip items={[
              { k: 'POOL', v: `${genoa} of ${real.length} Genoa`, fg: real.length && genoa === real.length ? 'var(--ok)' : undefined },
              { k: 'HARD_LIMIT', v: '1 live recording per machine' },
              { k: 'COLD START', v: '~40 s' },
              { k: 'STARTED', v: String(real.filter((m) => m.state === 'started').length) },
              { k: 'STOPPED', v: String(real.filter((m) => m.state === 'stopped').length) },
              { k: 'UPDATES', v: `${pl(waiting.length, 'engine')} waiting to update`, fg: waiting.length ? 'var(--conf-mid)' : undefined },
            ]} acts={stripActs} />
          </Block>
        </Cell>
        {running && <Cell span={12}><JobBanner job={running} machines={both} ctx={ctx} /></Cell>}
        <Cell span={12}>
          <Block title={`${name} · ${appName(env, 'engine-en')}`} count={pl(real.length, 'machine')} asOf={hhmmss(list.at)} stale={stale(list.ageS, 5)}
            sub="Class is re-read at every start: a stopped machine can come back on a different host. A machine being probed or created has no public service, so the proxy cannot route a stream to it."
            loading={list.loading && !list.data}
            error={flyErr && !list.data ? { title: flyErr.title, text: flyErr.text, retry: list.reload } : null}
            empty={list.data && !both.length ? `No machines in ${name}.` : undefined}
            note={list.data?.flyError && list.data.machines.length ? `Fly: ${list.data.flyError}` : undefined}>
            <Table cols={machCols('engine')} minWidth={980} mobile={mobile}
              rows={both.map((m) => {
                const english = m.app.endsWith('-engine-en');
                const app = english ? 'engine-en' : 'engine';
                return machRow(m, 'engine', env, english ? en.acts(m) : acts(m), { probing: (english ? en.probing : probing).has(m.id), onOpen: () => ctx.openDrawer(`machine:${app}:${m.id}`, <MachineDrawer app={app} id={m.id} />) });
              })} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
